import type { MultiTonePalette, Vibe } from './zod.js';

const HOUSE: MultiTonePalette = {
  warmTones: ['#F5F0E8', '#EDE6DB'],
  coolTones: ['#C9BCAC'],
  surfaceVariants: ['#F5F0E8', '#EDE8DF'],
  accent: '#D4845C',
  ink: '#201A16',
  muted: '#5C5348',
};

/** Dark text on light/medium accents (matches signature.css default). */
export const ON_ACCENT_DARK = '#241C16';
/** Light text on dark accents. */
export const ON_ACCENT_LIGHT = '#F5F0E8';

/**
 * House palette. Vibe is ignored at render; accent can still be overridden
 * per-lead on the call sheet.
 */
export function generateMultiTonePalette(_vibe: Vibe): MultiTonePalette {
  return {
    warmTones: [...HOUSE.warmTones],
    coolTones: [...HOUSE.coolTones],
    surfaceVariants: [...HOUSE.surfaceVariants],
    accent: HOUSE.accent,
    ink: HOUSE.ink,
    muted: HOUSE.muted,
  };
}

export function isStarkHex(hex: string): boolean {
  const u = hex.toUpperCase().replace(/^#/, '');
  const six = u.length === 3 ? u.split('').map((c) => c + c).join('') : u.slice(0, 6);
  return six === 'FFFFFF' || six === '000000';
}

function parseHex(input: string): { r: number; g: number; b: number } {
  let h = String(input).trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length === 8) h = h.slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(h)) {
    throw new Error(`Invalid hex color: "${input}"`);
  }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function relativeLuminance(hex: string): number {
  const { r, g, b } = parseHex(hex);
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two hex colors. */
export function contrastRatio(fgHex: string, bgHex: string): number {
  const a = relativeLuminance(fgHex);
  const b = relativeLuminance(bgHex);
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Foreground color for text/icons on an accent background (buttons, CTAs).
 * Picks dark or light house ink/paper whichever meets 4.5:1; prefers higher ratio.
 */
export function pickOnAccentColor(accentHex: string): string {
  const darkRatio = contrastRatio(ON_ACCENT_DARK, accentHex);
  const lightRatio = contrastRatio(ON_ACCENT_LIGHT, accentHex);
  const darkOk = darkRatio >= 4.5;
  const lightOk = lightRatio >= 4.5;
  if (darkOk && lightOk) return darkRatio >= lightRatio ? ON_ACCENT_DARK : ON_ACCENT_LIGHT;
  if (darkOk) return ON_ACCENT_DARK;
  if (lightOk) return ON_ACCENT_LIGHT;
  return darkRatio >= lightRatio ? ON_ACCENT_DARK : ON_ACCENT_LIGHT;
}

const STARK_MESSAGE =
  'Pure white (#FFFFFF) and pure black (#000000) are forbidden. Soften them.';

/** Client-side palette issues before Site DNA Zod parse (call sheet / wizard). */
export function validateMultiTonePalette(p: MultiTonePalette): string[] {
  const issues: string[] = [];
  const flat = [
    p.accent,
    ...(p.ink ? [p.ink] : []),
    ...(p.muted ? [p.muted] : []),
    ...p.warmTones,
    ...p.coolTones,
    ...p.surfaceVariants,
  ];
  if (flat.some(isStarkHex)) issues.push(STARK_MESSAGE);
  if (p.warmTones.length === 0 && p.coolTones.length === 0) {
    issues.push('Add at least one warm or cool tone.');
  }
  return issues;
}
